import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { BankInfoComponent } from '../add/bank-info/bank-info.component';
import { DocumentationComponent } from '../add/documentation/documentation.component';
import { TranslateModule } from '@ngx-translate/core';
import { PaymentAccountsService } from 'core/services/payment-accounts.service';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import {
  Account,
  Document,
  DocumentItem,
  DocumentTypes,
  IBankInfo,
  IDocument,
  IDocuments,
  UserDocument,
  UserDocumentType
} from 'shared/types/payment-account.interface';
import { tap } from 'rxjs/operators';
import { KorviuLoaderComponent } from 'shared/components/korviu-loader/korviu-loader.component';
import { LocalStorageKeys } from 'shared/types/local-storage-keys.enum';
import { NavigationService } from 'core/services/navigation.service';
import { KorviuButtonComponent } from 'shared/components/korviu-button/korviu-button.component';
import { CustomMessageService } from 'core/services/custom-message.service';
import { TranslateService } from '@ngx-translate/core';
import { paymentAccountSubmitErrorKey } from '../payment-account-error.helpers';

@Component({
  selector: 'app-details',
  standalone: true,
  imports: [BankInfoComponent, DocumentationComponent, TranslateModule, KorviuLoaderComponent, KorviuButtonComponent],
  templateUrl: './details.component.html',
  styleUrl: './details.component.scss'
})
export class DetailsComponent implements OnInit {
  private _paymentAccountsService = inject(PaymentAccountsService);
  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private _destroyRef = inject(DestroyRef);
  private _navigationService = inject(NavigationService);
  private _messageService = inject(CustomMessageService);
  private _translateService = inject(TranslateService);

  public isLoading = signal<boolean>(false);
  public isReloading = signal<boolean>(false);
  public paymentAccount = signal<Account>(null);
  public documentation = signal<IDocuments>({ officialIdFiles: [], taxFiles: [] });
  public overallStatus = signal<string>('');

  public bankInfo: IBankInfo = null;
  public idEdit: boolean;
  public taxEdit: boolean;

  public get showDetailsButtons(): boolean {
    return this.idEdit || this.taxEdit || this.bankInfo?.status === 'DECLINED' || !this.bankInfo?.status;
  }

  public bankInfoIsValid: boolean;
  public get documentationIsValid(): boolean {
    const taxIsValid =
      !this.documentation()?.tax.some((d: IDocument) => d?.status === 'DECLINED') &&
      this.documentation()?.tax?.length >= 1;
    const officialIdIsValid =
      !this.documentation()?.officialId.some((d: IDocument) => d?.status === 'DECLINED') &&
      this.documentation()?.officialId?.length >= 1;

    if (this.bankInfo?.account_type === 'US') {
      return officialIdIsValid;
    }

    return officialIdIsValid && taxIsValid;
  }
  public get submitButtonEnabled(): boolean {
    return this.bankInfoIsValid && this.documentationIsValid;
  }

  public ngOnInit(): void {
    const paymentAccountId = +this._route.snapshot.paramMap.get('id');
    this._loadPaymentAccountDetails(paymentAccountId);
  }

  public onSubmit(): void {
    if (this.isReloading()) {
      return;
    }
    this.isReloading.set(true);
    this._uploadDocuments()
      .pipe(
        switchMap((documentIds: number[]) => this._updatePaymentAccount(documentIds)),
        finalize(() => {
          this.isReloading.set(false);
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        error: (error: unknown) => {
          this._messageService.error(this._translateService.instant(paymentAccountSubmitErrorKey(error)));
        }
      });
  }

  public onBack(): void {
    this._navigationService.goBack();
  }

  private _updatePaymentAccount(documentIds: number[]): Observable<string> {
    return this._paymentAccountsService
      .updatePaymentAccount(
        this.paymentAccount()?.id,
        { bankInfo: this.bankInfo, documentation: this.documentation() },
        documentIds
      )
      .pipe(
        tap(() => {
          localStorage.setItem(LocalStorageKeys.SHOW_DOCUMENTS_RECEIVED_MODAL, 'true');
          this._router.navigate(['summary'], { relativeTo: this._route.parent });
        })
      );
  }

  private _uploadDocuments(): Observable<number[]> {
    const alreadyUploaded = [
      ...this.documentation()?.officialIdFiles?.filter((file: any) => !file.thumbUrl),
      ...this.documentation()?.taxFiles.filter((file: any) => !file.thumbUrl)
    ];

    return this._getDocumentTypes().pipe(
      switchMap((types: UserDocumentType[]) => {
        const officialIdFiles = this.documentation()?.officialIdFiles?.filter((file: any) => !!file.thumbUrl) || [];
        const taxFiles = this.documentation()?.taxFiles.filter((file: any) => !!file.thumbUrl) || [];

        const officialIdUserDocuments = officialIdFiles?.map((file: any) => {
          const documentType = types.find((t: UserDocumentType) => t.code === DocumentTypes.ID);
          const userDocument: UserDocument = {
            file_name: file?.name,
            blob: file?.thumbUrl,
            user_document_type_id: documentType?.id,
            user_document_status_id: 4
          };
          return userDocument;
        });
        const taxUserDocuments = taxFiles?.map((file: any) => {
          const documentType = types.find((t: UserDocumentType) => t.code === DocumentTypes.TAX);
          const userDocument: UserDocument = {
            file_name: file?.name,
            blob: file?.thumbUrl,
            user_document_type_id: documentType?.id,
            user_document_status_id: 4
          };
          return userDocument;
        });
        const uploadDocuments = [...officialIdUserDocuments, ...taxUserDocuments].map((document: UserDocument) => {
          return this._paymentAccountsService.createUserDocument(document);
        });

        return uploadDocuments?.length ? forkJoin(uploadDocuments) : of([]);
      }),
      map((documents: any[]) => [
        ...alreadyUploaded?.map((d: any) => d?.id as number),
        ...documents?.map((d: any) => d?.id as number)
      ])
    );
  }

  private _getDocumentTypes(): Observable<UserDocumentType[]> {
    return this._paymentAccountsService.getDocumentTypes();
  }

  private _loadPaymentAccountDetails(id: number): void {
    this.isLoading.set(true);
    this._paymentAccountsService
      .getPaymentAccountById(id)
      .pipe(
        switchMap((paymentAccount: Account) => this._fillDocumentFiles(paymentAccount, DocumentTypes.ID)),
        switchMap((paymentAccount: Account) => this._fillDocumentFiles(paymentAccount, DocumentTypes.TAX)),
        tap((paymentAccount: Account) => this.paymentAccount.set(paymentAccount)),
        tap((paymentAccount: Account) => this._setBankInfo(paymentAccount)),
        tap((paymentAccount: Account) => this._setDocumentation(paymentAccount)),
        tap((paymentAccount: Account) => {
          this.overallStatus.set(paymentAccount.overall_status);
        }),
        finalize(() => {
          this.isLoading.set(false);
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        error: () => {
          this._messageService.error(this._translateService.instant('profile.addAccount.loadError'));
        }
      });
  }

  private _setBankInfo(paymentAccount: Account): void {
    const isUS = paymentAccount?.account_type === 'US';
    const currencyIds = { USD: 2, MXN: 1 };
    const bankInfo: IBankInfo = {
      currency: currencyIds[paymentAccount?.bank_information?.currency?.value],
      account_type: isUS ? 'US' : 'MX',
      bank_name: paymentAccount?.bank_information?.bank_name?.value,
      account_number: paymentAccount?.bank_information?.account_number?.value,
      routing_number: paymentAccount?.bank_information?.routing_number?.value,
      clabe_number: isUS ? '' : paymentAccount?.bank_information?.clabe_number?.value,
      status: paymentAccount?.bank_info_status,
      holder_full_name: paymentAccount?.account_holder_name,
      bank_information: paymentAccount.bank_information
    };
    this.bankInfo = bankInfo;
  }

  private _setDocumentation(paymentAccount: Account): void {
    const officialIdDocuments: IDocument[] = this._parseDocuments(paymentAccount.documents, DocumentTypes.ID);
    const taxDocuments: IDocument[] = this._parseDocuments(paymentAccount.documents, DocumentTypes.TAX);
    const documentation: IDocuments = {
      tax: taxDocuments,
      officialId: officialIdDocuments,
      taxFiles: taxDocuments.map((d: IDocument) => d.file),
      officialIdFiles: officialIdDocuments.map((d: IDocument) => d.file)
    };
    this.idEdit = officialIdDocuments?.some((d: IDocument) => d.status === 'DECLINED');
    this.taxEdit = taxDocuments?.some((d: IDocument) => d.status === 'DECLINED');

    this.documentation.set(documentation);
  }

  private _parseDocuments(documents: Document[], type: DocumentTypes): IDocument[] {
    const filteredDocuments = documents.filter((d: Document) => d.document_type.code === type);

    if (!!filteredDocuments?.length) {
      return filteredDocuments.map((d: DocumentItem) => {
        const idocument: IDocument = {
          file: d.file,
          name: d.file_name,
          url: d.url,
          url_expires_at: d.url_expires_at,
          status: d.status,
          id: d.id,
          rejectReason: d.reject_reason
        };
        return idocument;
      });
    }

    return [];
  }

  private _fillDocumentFiles(paymentAccount: Account, userDocumentType: DocumentTypes): Observable<Account> {
    const declinedDocuments = paymentAccount.documents.filter(
      (d: Document) => d.document_type.code === userDocumentType
    );
    let fileRequests: Observable<File[]>;

    if (declinedDocuments?.length) {
      fileRequests = forkJoin(
        declinedDocuments.map((di: DocumentItem) => {
          return this._paymentAccountsService.getDocumentFile(di);
        })
      ).pipe(
        tap((files: File[]) => {
          declinedDocuments.forEach((di: DocumentItem, i: number) => {
            di.file = files[i];
            di.file.id = di.id;
          });
        })
      );
    } else {
      fileRequests = of([]);
    }

    return fileRequests.pipe(map(() => paymentAccount));
  }
}
